import type {
  BranchesApiResponse,
  BranchHistoryResponse,
  CodeServerTokenResponse,
  SshKeysCheckResponse,
  SshTunnelInfo,
  SshTunnelResponse,
  TestResultsResponse,
} from '@/types/deployments';
import { apiRequest } from '@/lib/api';

export async function getBranches(projectId: string): Promise<BranchesApiResponse> {
  return apiRequest(`/api/project/${projectId}/branches`);
}

export async function getBranchHistory(
  projectId: string,
  branchId: number,
  page: number = 1,
  perPage: number = 10
): Promise<BranchHistoryResponse> {
  return apiRequest(
    `/api/project/${projectId}/branches/${branchId}/history?page=${page}&per_page=${perPage}`
  );
}

// Mailpit auth token
export interface MailpitTokenResponse {
  login_url: string;
  token: string;
}

export async function getMailpitToken(buildId: number): Promise<MailpitTokenResponse> {
  return apiRequest(`/api/v1/builds/${buildId}/mailpit-token`, {
    method: 'POST',
  });
}

// Code-server token
export async function getCodeServerToken(buildId: number): Promise<CodeServerTokenResponse> {
  return apiRequest(`/api/v1/builds/${buildId}/codeserver-token`, {
    method: 'POST',
  });
}

// Remote IDE / SSH tunnel
export async function checkUserSshKeys(): Promise<SshKeysCheckResponse> {
  return apiRequest('/api/v1/user/ssh-keys/check');
}

export async function getSshTunnel(buildId: number): Promise<SshTunnelResponse> {
  return apiRequest(`/api/v1/builds/${buildId}/ssh-tunnel`);
}

export async function createSshTunnel(
  buildId: number,
  usePersonalKey: boolean
): Promise<SshTunnelResponse> {
  try {
    const data = await apiRequest<SshTunnelResponse>(`/api/v1/builds/${buildId}/ssh-tunnel`, {
      method: 'POST',
      body: JSON.stringify({ use_personal_key: usePersonalKey }),
    });

    if (!data.success) {
      return {
        success: false,
        error: 'Something went wrong. Please try again.',
        debug_error: data.error,
      };
    }

    const tunnel = data.tunnel as SshTunnelInfo | undefined;
    return { ...data, tunnel };
  } catch (error) {
    return {
      success: false,
      error: 'Something went wrong. Please try again.',
      debug_error: error instanceof Error ? error.message : String(error),
    };
  }
}

// Connect As (Odoo)
export interface OdooUsersResponse {
  success: boolean;
  users?: Array<{ id: string; name: string }>;
  error?: string;
}

export interface ConnectAsResponse {
  success: boolean;
  auth_url?: string;
  error?: string;
}

export async function getOdooUsers(projectId: string, buildId: number): Promise<OdooUsersResponse> {
  return apiRequest(`/api/project/${projectId}/deployments/${buildId}/odoo-users`);
}

export async function connectAsOdooUser(
  projectId: string,
  buildId: number,
  userId: string
): Promise<ConnectAsResponse> {
  return apiRequest(`/api/project/${projectId}/deployments/${buildId}/connect-as`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

// Rebuild deployment
export async function rebuildDeployment(
  projectId: string,
  buildId: number
): Promise<{ success: boolean; message?: string; error?: string; new_build_id?: number }> {
  return apiRequest(`/api/project/${projectId}/builds/${buildId}/rebuild`, {
    method: 'POST',
  });
}

// Config API
export interface ConfigResponse {
  success: boolean;
  content?: string;
  container_name?: string;
  fqdn?: string;
  error?: string;
}

export async function getBranchConfig(projectId: string, branchId: number): Promise<ConfigResponse> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/config`);
}

export async function saveBranchConfig(
  projectId: string, 
  branchId: number, 
  content: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/config`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

// Branch Backups API
export interface BranchBackup {
  id: number;
  status: string;
  created_at: string | null;
  runtime_seconds: number | null;
  size_mb: number | null;
  odoo_version: string | null;
  commit_hash: string | null;
  comments: string | null;
  build: {
    id: number;
    fqdn: string;
    type: string;
    db_name: string;
  } | null;
}

export interface BranchBackupsResponse {
  success: boolean;
  backups: BranchBackup[];
  build: {
    id: number;
    fqdn: string;
    type: string;
    db_name: string;
    odoo_version: string | null;
    server_id: number;
    server_name: string | null;
  } | null;
  backup_storage_enabled: boolean;
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_prev: boolean;
    has_next: boolean;
  };
  error?: string;
}

export async function getBranchBackups(
  projectId: string,
  branchId: number,
  page: number = 1
): Promise<BranchBackupsResponse> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/backups?page=${page}`);
}

export async function createBranchBackup(
  projectId: string,
  branchId: number,
  comment?: string
): Promise<{ success: boolean; backup_id?: number; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/backups`, {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
}

// Backup Schedule API for branches
export interface BranchBackupSchedule {
  id: number;
  name: string;
  frequency: string;
  hours_interval: number;
  day_of_week: number;
  day_of_month: number;
  hour: number;
  minute: number;
  cron_expression: string | null;
  retention_count: number;
  active: boolean;
  last_run: string | null;
  next_run: string | null;
  created_at: string | null;
  comments: string | null;
}

export interface BranchBackupSchedulesResponse {
  success: boolean;
  schedules: BranchBackupSchedule[];
  build: { id: number; fqdn: string; db_name: string } | null;
  backup_storage_enabled: boolean;
  error?: string;
}

export async function getBranchBackupSchedules(
  projectId: string, 
  branchId: number
): Promise<BranchBackupSchedulesResponse> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/backup-schedules`);
}

export async function createBranchBackupSchedule(
  projectId: string,
  branchId: number,
  data: {
    name: string;
    frequency: string;
    retention_count: number;
    comments?: string;
    hours_interval?: number;
    minute?: number;
    daily_time?: string;
    weekly_time?: string;
    day_of_week?: number;
    monthly_time?: string;
    day_of_month?: number;
    cron_expression?: string;
  }
): Promise<{ success: boolean; schedule_id?: number; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/backup-schedules`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Deployment Settings API
export interface DeploymentDetails {
  id: number;
  fqdn: string;
  db_name: string;
  container_name: string;
  type: string;
  status: string;
  heartbeat_status: string;
  automatic_odoo_updates: boolean;
  cloudflare_proxied: boolean;
  odoo_version: string | null;
  odoo_git_version: string | null;
  odoo_enterprise: boolean;
  odoo_enterprise_git_version: string | null;
  server: {
    id: number;
    name: string;
    ip_address: string;
  } | null;
}

export interface DeploymentSettingsResponse {
  success: boolean;
  deployment: DeploymentDetails | null;
  branch: {
    id: number;
    name: string;
    branch_type: string;
    odoo_version: string | null;
  };
  project: {
    odoo_version: string;
    odoo_enterprise_code: boolean;
  };
  error?: string;
}

export async function getDeploymentSettings(
  projectId: string, 
  branchId: number
): Promise<DeploymentSettingsResponse> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/deployment`);
}

export async function manageDeploymentService(
  projectId: string,
  branchId: number,
  action: 'start' | 'stop' | 'restart'
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/deployment/service`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

export async function updateDeploymentSettings(
  projectId: string,
  branchId: number,
  settings: { automatic_odoo_updates?: boolean }
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/deployment/settings`, {
    method: 'POST',
    body: JSON.stringify(settings),
  });
}

export async function deleteDeployment(
  projectId: string,
  branchId: number,
  options: { delete_github_branch?: boolean; delete_associated_backups?: boolean }
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/deployment`, {
    method: 'DELETE',
    body: JSON.stringify(options),
  });
}

export async function updateDeploymentDomain(
  projectId: string,
  branchId: number,
  newFqdn: string,
  cloudflareProxied: boolean = true
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/deployment/domain`, {
    method: 'POST',
    body: JSON.stringify({ new_fqdn: newFqdn, cloudflare_proxied: cloudflareProxied }),
  });
}

export async function updateCloudflareProxy(
  projectId: string,
  branchId: number,
  cloudflareProxied: boolean
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/deployment/cloudflare-proxy`, {
    method: 'POST',
    body: JSON.stringify({ cloudflare_proxied: cloudflareProxied }),
  });
}

export async function changeOdooVersion(
  projectId: string,
  branchId: number,
  newVersion: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/odoo-version`, {
    method: 'POST',
    body: JSON.stringify({ new_odoo_version: newVersion }),
  });
}

export async function checkDomainAvailability(
  projectId: string,
  domain: string
): Promise<{ available: boolean; reason?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/check-domain-availability?domain=${encodeURIComponent(domain)}`);
}

// PostgreSQL Remote Access API
export interface PostgresConnectionDetails {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string | null;
  connection_string?: string;
  psql_command?: string;
}

export interface PostgresAccessResponse {
  success: boolean;
  is_development: boolean;
  has_build: boolean;
  postgres_enabled: boolean;
  postgres_status: 'disabled' | 'enabled' | 'pending' | 'error';
  ip_whitelist: string[];
  connection_details: PostgresConnectionDetails | null;
  build: {
    id: number;
    fqdn: string;
    db_name: string;
    container_name: string;
  } | null;
  error?: string;
}

export async function getPostgresAccess(
  projectId: string,
  branchId: number
): Promise<PostgresAccessResponse> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/postgres`);
}

export async function togglePostgresAccess(
  projectId: string,
  branchId: number,
  enable: boolean
): Promise<{ success: boolean; message?: string; postgres_status?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/postgres/toggle`, {
    method: 'POST',
    body: JSON.stringify({ enable }),
  });
}

export async function managePostgresWhitelist(
  projectId: string,
  branchId: number,
  action: 'add' | 'remove',
  ip: string
): Promise<{ success: boolean; message?: string; ip_whitelist?: string[]; error?: string }> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/postgres/whitelist`, {
    method: 'POST',
    body: JSON.stringify({ action, ip }),
  });
}

// Create Deployment API
export interface DeploymentFormData {
  success: boolean;
  servers: Array<{
    id: number;
    name: string;
    ip_address: string;
    has_production: boolean;
    has_staging: boolean;
  }>;
  // Branches available for direct selection (no active deployments)
  available_branches: Array<{
    id: number;
    name: string;
    branch_type: string;
  }>;
  // All branches (for forking source selection)
  all_branches: Array<{
    id: number;
    name: string;
    branch_type: string;
  }>;
  has_production: boolean;
  has_servers: boolean;
  provisioning_servers: number;
  developer_branches_enabled: boolean;
  backup_storage_enabled: boolean;
  odoo_version: string;
  default_domain: string;
  default_database: string;
  default_password: string;
  error?: string;
}

export async function getDeploymentFormData(projectId: string): Promise<DeploymentFormData> {
  return apiRequest(`/api/project/${projectId}/deployments/data`);
}

export interface CreateDeploymentParams {
  branch_type: 'production' | 'staging' | 'development';
  branch_id?: number;
  server_id?: number;
  domain_name: string;
  database_name: string;
  admin_password?: string;
  create_backup_schedule?: boolean;
  is_fork?: boolean;
  source_branch_id?: number;
  new_branch_name?: string;
}

export async function createDeployment(
  projectId: string,
  params: CreateDeploymentParams
): Promise<{ success: boolean; message?: string; build_id?: number; domain_name?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/deployments/create`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// Test Results
export async function getTestResults(
  projectId: string,
  branchId: number,
  buildId: number
): Promise<TestResultsResponse> {
  return apiRequest(`/api/project/${projectId}/branches/${branchId}/builds/${buildId}/test-results`);
}
